/*
 * dgOperatingLevel — calculation engine for the DG Operating Level & Capacity
 * Utilisation report.
 * ---------------------------------------------------------------------------
 * WHAT THIS COMPUTES
 *
 * The base measurement is the same full-tank-to-full-tank (F2F) interval the
 * rest of the fuel reports use: fuel burnt between two "filled to full" events,
 * against the meter movement over that same interval. Nothing here re-derives
 * consumption from anything other than the meters recorded on those two entries.
 *
 * For a DG (an asset carrying a KWH meter) each interval yields:
 *
 *   Operating Level  = KWH run / HOURS run          -> KWH/Hour, i.e. average kW
 *   Efficiency       = KWH run / litres             -> kWh/L
 *   Burn rate        = litres / HOURS run           -> L/hour
 *   Utilisation      = average kW / (rated kVA x power factor)
 *
 * Operating Level is NOT redefined here — it is `computeOperatingLevel` from
 * fuelService, the same function the Fuel Alert Analysis F2F trend uses, so a
 * row in this report and the same row in that trend can never disagree.
 *
 * From the per-interval set the engine derives, per DG: total hours, energy,
 * fuel, average kW, utilisation, the P95 interval-average kW, the capacity that
 * P95 implies, and — when the fleet fuel curve could be fitted — the litres a
 * smaller set would have saved over the same delivered load and hours.
 *
 * DATA RELIABILITY (see the global AssetPro rules)
 *   - Nothing is defaulted, inferred or back-filled. Every interval that cannot
 *     be measured is dropped with an explicit reason string, and the reasons are
 *     counted and reported rather than silently swallowed.
 *   - Rated kVA is NOT invented. It is supplied by the caller (confirmed by a
 *     human against a parsed suggestion). An asset whose model has no confirmed
 *     kVA is excluded from every capacity figure and listed as excluded.
 *   - Diesel price and DG purchase prices are business data with no defensible
 *     default, so they are optional inputs. Every money figure is null until the
 *     corresponding price is supplied — never zero.
 *   - A DG whose measured efficiency is outside the plausible band is marked
 *     meter-suspect and claims NO saving, instead of producing a confident
 *     number from readings that are probably wrong.
 *
 * KNOWN LIMITATION, stated wherever the output is shown: the kW here is an
 * interval AVERAGE between refuelling events, not an instantaneous demand peak.
 * A non-uniform load, motor starts or step loads can be materially higher, so
 * the recommended size is a screening candidate, not a procurement decision.
 */

import {
  METER_LABELS,
  computeOperatingLevel,
  findMeterByType,
  getMeterRunsBetween,
} from './fuelService';
import { parseCapacity } from './capacityParser';

// --- assumptions ------------------------------------------------------------

/*
 * Engineering assumptions. These are screening parameters, not business data:
 * they are shown on screen and editable, and every figure derived from them
 * carries the value that was used. They are deliberately separate from prices,
 * which have no default at all.
 */
export const DEFAULT_ASSUMPTIONS = {
  // kVA -> kW. 0.8 is the standard genset nameplate power factor.
  powerFactor: 0.8,
  // A replacement is sized so the P95 interval-average load sits at this share
  // of its rated kW, leaving headroom for the peaks this data cannot see.
  targetLoading: 0.75,
  // Above this, utilisation is treated as implausible (bad meter or bad rating)
  // and the interval is dropped from the load analysis rather than believed.
  maxPlausibleUtilisation: 1.1,
  // Plausible kWh/L band. Used to (a) screen intervals out of the fuel-curve
  // regression and (b) flag a DG whose overall efficiency is not believable.
  minPlausibleEfficiency: 1.5,
  maxPlausibleEfficiency: 6.0,
  // Share of the largest absolute residuals dropped before the final fuel-curve
  // refit, so a handful of mis-keyed intervals cannot set the coefficients.
  residualTrimPercent: 5,
  // A period shorter than this is not annualised — scaling a few days to a year
  // would state a yearly figure the data cannot support.
  minAnnualisationDays: 30,
  // Below this many valid intervals a P95 is reported but flagged as thin.
  minIntervalsForP95: 5,
  // Below this many valid fuel intervals the fleet curve is refused outright.
  minObservationsForCurve: 10,
};

/*
 * Standard genset ratings available in the market, used to round a computed kVA
 * requirement up to a size that can actually be bought. This is an equipment
 * ladder, not client data — and it is editable in the UI, because a site may be
 * restricted to the sizes one supplier lists.
 */
export const STANDARD_DG_SIZES_KVA = [
  15, 20, 25, 30, 40, 50, 62.5, 75, 82.5, 100, 125, 140, 160, 180, 200, 225, 250,
  320, 380, 400, 500, 600, 625, 750, 1000, 1250, 1500, 2000,
];

// --- small numeric helpers --------------------------------------------------

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Linear-interpolated percentile (the PERCENTILE.INC convention), so the figure
 * matches what the same numbers give in Excel.
 *
 * @param {number[]} values unsorted numeric values
 * @param {number} p fraction in [0, 1]
 * @returns {number|null} null when there is nothing to take a percentile of
 */
export const percentile = (values, p) => {
  const sorted = (values || []).filter(isNum).slice().sort((a, b) => a - b);
  if (sorted.length === 0) return null;
  if (sorted.length === 1) return sorted[0];
  const rank = p * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (rank - lo) * (sorted[hi] - sorted[lo]);
};

// --- rated capacity ---------------------------------------------------------

/*
 * Where a kVA suggestion may be read from, in the order they are trusted.
 * AssetPro has no capacity field on the asset or the asset model, so the rating
 * only exists inside free text. Every suggestion is therefore a SUGGESTION: it
 * is shown with the exact string it came from and must be confirmed by a human
 * before any utilisation figure is produced from it.
 */
export const KVA_SOURCE_FIELDS = ['assetSubType', 'assetModel'];

/**
 * Parse a kVA rating out of one or more free-text strings.
 *
 * @param {Array<{field: string, text: string}>} candidates in priority order
 * @returns {{value: number, field: string, text: string}|null} null when no
 *          candidate contains a kVA rating — never a guessed number.
 */
export const suggestKvaFromText = (candidates) => {
  for (const candidate of candidates || []) {
    if (!candidate || !candidate.text) continue;
    const parsed = parseCapacity(candidate.text);
    // Only a kVA rating counts. A "400 KW" or "25 HP" string is a different
    // quantity and converting it here would be an invented number.
    if (parsed.status === 'OK' && parsed.unit === 'KVA' && parsed.value > 0) {
      return { value: parsed.value, field: candidate.field, text: candidate.text };
    }
  }
  return null;
};

/**
 * Smallest standard size that carries the requirement, never larger than what is
 * installed today.
 *
 * @returns {{kva: number|null, reason: string|null}}
 *   kva === currentKva means "keep the existing set".
 */
export const recommendSize = (requiredKva, currentKva, sizes = STANDARD_DG_SIZES_KVA) => {
  if (!isNum(requiredKva) || requiredKva <= 0) {
    return { kva: null, reason: 'No load requirement could be measured' };
  }
  if (!isNum(currentKva) || currentKva <= 0) {
    return { kva: null, reason: 'Rated kVA not confirmed' };
  }
  const ladder = (sizes || []).filter(isNum).filter((s) => s > 0).slice().sort((a, b) => a - b);
  if (ladder.length === 0) {
    return { kva: null, reason: 'No standard sizes defined' };
  }
  if (requiredKva > currentKva) {
    // The measured P95 already needs the installed rating (or more). Downsizing
    // is not on the table and no saving may be claimed.
    return { kva: currentKva, reason: 'P95 load already needs the installed rating' };
  }
  const candidate = ladder.find((s) => s >= requiredKva && s <= currentKva);
  if (candidate === undefined) {
    // Requirement fits under the installed set but no listed size does — e.g.
    // the ladder's smallest entry is above the installed rating.
    return { kva: currentKva, reason: 'No standard size between the requirement and the installed rating' };
  }
  return { kva: candidate, reason: null };
};

// --- per-interval measurement ----------------------------------------------

const meterIsFaulty = (mileage, type) => {
  const meter = findMeterByType(mileage, type);
  return !!(meter && meter.isFaultyMeter === true);
};

const entryMs = (entry) => {
  const raw = entry && (entry.date || entry.datetime);
  if (raw === null || raw === undefined) return null;
  const ms = raw instanceof Date ? raw.getTime() : new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Does this asset's fuel history carry a KWH meter at all? That is the whole
 * definition of "DG" for this report — an asset with KWH defined as a meter
 * reading — and it is decided by the meter's own name, never by asset type.
 */
export const hasKwhMeter = (entries) =>
  (entries || []).some((e) => findMeterByType(e && e.mileage, 'KWH') !== null);

/**
 * Build every measurable full-tank-to-full-tank interval for one DG.
 *
 * Fuel for an interval = the quantity on the closing full tank plus every entry
 * (full tank or not) recorded strictly between the two full tanks — the same
 * rule the Full Tank Consumption Report uses.
 *
 * @param {Object} params
 * @param {string} params.assetCode
 * @param {Array} params.entries raw /fuel/mileage-report entries for this asset
 * @param {number|null} params.ratedKva confirmed rating, or null when unconfirmed
 * @param {Object} params.assumptions
 * @param {number} [params.rangeStartMs] only report intervals CLOSING in range
 * @param {number} [params.rangeEndMs]
 * @returns {{intervals: Array, dropped: Array<{reason: string, currentDate: string}>}}
 */
export const buildAssetIntervals = ({
  assetCode,
  entries,
  ratedKva,
  assumptions = DEFAULT_ASSUMPTIONS,
  rangeStartMs = null,
  rangeEndMs = null,
}) => {
  const intervals = [];
  const dropped = [];

  const all = (entries || [])
    .map((e) => ({ entry: e, ms: entryMs(e) }))
    .filter((e) => e.ms !== null)
    .sort((a, b) => a.ms - b.ms);

  const fullTanks = all.filter((e) => e.entry.isFullTank === true);

  for (let i = 1; i < fullTanks.length; i += 1) {
    const current = fullTanks[i];
    const previous = fullTanks[i - 1];

    // Only intervals CLOSING inside the requested window are reported. The
    // opening full tank is allowed to sit before the window — that is what makes
    // the first interval in the period measurable at all.
    if (rangeStartMs !== null && current.ms < rangeStartMs) continue;
    if (rangeEndMs !== null && current.ms > rangeEndMs) continue;

    const closingDate = new Date(current.ms).toISOString();
    const drop = (reason) => dropped.push({ assetCode, reason, currentMs: current.ms, currentDate: closingDate });

    // Every entry whose litres belong to this interval: the closing full tank
    // plus anything recorded strictly between the two full tanks.
    const fuelEntries = all.filter((e) => e.ms === current.ms || (e.ms > previous.ms && e.ms < current.ms));
    const missingQuantity = fuelEntries.filter((e) => num(e.entry.quantity) === null);

    const runs = getMeterRunsBetween(current.entry.mileage, previous.entry.mileage);
    const level = computeOperatingLevel(runs);

    const faulty =
      meterIsFaulty(current.entry.mileage, 'KWH') ||
      meterIsFaulty(previous.entry.mileage, 'KWH') ||
      meterIsFaulty(current.entry.mileage, 'HOURS') ||
      meterIsFaulty(previous.entry.mileage, 'HOURS');

    if (faulty) {
      drop('Faulty meter flagged on one of the two readings');
      continue;
    }
    if (!isNum(runs.KWH)) {
      drop('No KWH run — the meter is missing on one of the two full tanks');
      continue;
    }
    if (!isNum(runs.HOURS)) {
      drop('No HOURS run — the meter is missing on one of the two full tanks');
      continue;
    }
    // Guard the pairing rather than assume it: Operating Level must have come
    // out as KWH over Hours for this interval to mean average kW.
    if (!level || level.unitLabel !== 'KWH/Hour') {
      drop(level && level.reason ? level.reason : 'Operating Level is not KWH/Hour');
      continue;
    }
    if (level.value === null) {
      drop(level.reason || 'Operating Level could not be derived');
      continue;
    }
    // An entry with no quantity is not an entry of 0 L. Summing it as zero would
    // understate the litres and overstate kWh/L, so the interval is dropped and
    // the voucher named instead.
    if (missingQuantity.length) {
      drop(
        `Fuel quantity missing on an entry in the interval (${missingQuantity
          .map((e) => e.entry.voucherNo || 'entry with no voucher')
          .join(', ')})`
      );
      continue;
    }
    const fuelL = fuelEntries.reduce((sum, e) => sum + num(e.entry.quantity), 0);
    if (fuelL <= 0) {
      drop('No fuel recorded between the two full tanks');
      continue;
    }

    const avgKw = level.value;
    const hoursRun = runs.HOURS;
    const kwhRun = runs.KWH;
    const efficiencyKwhPerL = kwhRun / fuelL;
    const litresPerHour = fuelL / hoursRun;
    const ratedKw = isNum(ratedKva) && ratedKva > 0 ? ratedKva * assumptions.powerFactor : null;
    const utilisation = ratedKw ? avgKw / ratedKw : null;

    if (utilisation !== null && utilisation > assumptions.maxPlausibleUtilisation) {
      drop(
        `Utilisation ${(utilisation * 100).toFixed(0)}% is above the plausible maximum ` +
          `${(assumptions.maxPlausibleUtilisation * 100).toFixed(0)}%`
      );
      continue;
    }

    // Whether this interval may inform the fleet fuel curve. Failing this does
    // NOT remove the interval from the load analysis — it only keeps a doubtful
    // litres/hour observation out of the regression.
    let fuelCurveReason = null;
    if (efficiencyKwhPerL < assumptions.minPlausibleEfficiency) {
      fuelCurveReason = `Efficiency ${efficiencyKwhPerL.toFixed(2)} kWh/L is below the plausible minimum`;
    } else if (efficiencyKwhPerL > assumptions.maxPlausibleEfficiency) {
      fuelCurveReason = `Efficiency ${efficiencyKwhPerL.toFixed(2)} kWh/L is above the plausible maximum`;
    } else if (!isNum(ratedKva) || ratedKva <= 0) {
      fuelCurveReason = 'Rated kVA not confirmed — cannot attribute litres to capacity';
    }

    intervals.push({
      key: `${assetCode}_${current.ms}`,
      assetCode,
      previousMs: previous.ms,
      currentMs: current.ms,
      previousDate: new Date(previous.ms).toISOString(),
      currentDate: closingDate,
      voucherNo: current.entry.voucherNo || null,
      openingVoucherNo: previous.entry.voucherNo || null,
      // How many entries the litres were summed from (closing full tank + top-ups),
      // so the fuel figure can be traced back to its vouchers.
      fuelEntryCount: fuelEntries.length,
      fuelL,
      kwhRun,
      hoursRun,
      avgKw,
      efficiencyKwhPerL,
      litresPerHour,
      ratedKva: isNum(ratedKva) ? ratedKva : null,
      utilisation,
      operatingLevelUnit: level.unitLabel,
      validForFuelCurve: fuelCurveReason === null,
      fuelCurveReason,
    });
  }

  return { intervals, dropped };
};

// --- fleet fuel curve -------------------------------------------------------

/*
 * Two-term least squares through the origin:
 *
 *     litres/hour  =  a x installed kVA  +  b x average kW
 *
 * `a` is the litres/hour a set burns purely for being that size (windage,
 * friction, the fixed part of the burn); `b` is the litres/hour per kW actually
 * delivered. Isolating `a` is what lets a capacity-only saving be stated while
 * holding delivered load and operating hours unchanged.
 *
 * There is no intercept on purpose: a set that is neither installed nor loaded
 * burns nothing, and an intercept would let the fit buy accuracy with a
 * physically meaningless constant.
 */
const solve2x2 = (s11, s12, s22, s1y, s2y) => {
  const det = s11 * s22 - s12 * s12;
  if (!Number.isFinite(det) || Math.abs(det) < 1e-12) return null;
  return {
    a: (s22 * s1y - s12 * s2y) / det,
    b: (s11 * s2y - s12 * s1y) / det,
  };
};

const fitOnce = (obs) => {
  let s11 = 0;
  let s12 = 0;
  let s22 = 0;
  let s1y = 0;
  let s2y = 0;
  obs.forEach((o) => {
    s11 += o.kva * o.kva;
    s12 += o.kva * o.avgKw;
    s22 += o.avgKw * o.avgKw;
    s1y += o.kva * o.litresPerHour;
    s2y += o.avgKw * o.litresPerHour;
  });
  return solve2x2(s11, s12, s22, s1y, s2y);
};

/**
 * Fit the fleet fuel curve, then refit after dropping the largest residuals.
 *
 * @param {Array<{kva:number, avgKw:number, litresPerHour:number}>} observations
 * @param {Object} assumptions
 * @returns {{ok: boolean, reason: string|null, capacityCoefficient: number|null,
 *            loadCoefficient: number|null, rSquared: number|null,
 *            nInput: number, nFitted: number, nTrimmed: number,
 *            capacityCoefficientUsable: boolean}}
 */
export const fitFleetFuelCurve = (observations, assumptions = DEFAULT_ASSUMPTIONS) => {
  const obs = (observations || []).filter(
    (o) => o && isNum(o.kva) && o.kva > 0 && isNum(o.avgKw) && o.avgKw >= 0 && isNum(o.litresPerHour) && o.litresPerHour > 0
  );

  const fail = (reason) => ({
    ok: false,
    reason,
    capacityCoefficient: null,
    loadCoefficient: null,
    rSquared: null,
    nInput: obs.length,
    nFitted: 0,
    nTrimmed: 0,
    capacityCoefficientUsable: false,
  });

  if (obs.length < assumptions.minObservationsForCurve) {
    return fail(
      `Only ${obs.length} usable interval(s); at least ${assumptions.minObservationsForCurve} are needed to fit a fleet fuel curve`
    );
  }

  const first = fitOnce(obs);
  if (!first) {
    return fail('The installed kVA and average kW columns are not independent enough to separate the two coefficients');
  }

  // Drop the largest |residual| share, then refit on what is left.
  const withResiduals = obs
    .map((o) => ({ ...o, residual: o.litresPerHour - (first.a * o.kva + first.b * o.avgKw) }))
    .sort((x, y) => Math.abs(x.residual) - Math.abs(y.residual));
  const trimCount = Math.min(
    Math.round((obs.length * (assumptions.residualTrimPercent || 0)) / 100),
    Math.max(0, obs.length - assumptions.minObservationsForCurve)
  );
  const kept = trimCount > 0 ? withResiduals.slice(0, withResiduals.length - trimCount) : withResiduals;

  const final = fitOnce(kept);
  if (!final) {
    return fail('The refit after residual trimming could not be solved');
  }

  // Uncentered R^2 — the right measure for a through-origin model, where the
  // total sum of squares is taken about zero rather than about the mean.
  let ssRes = 0;
  let ssTot = 0;
  kept.forEach((o) => {
    const predicted = final.a * o.kva + final.b * o.avgKw;
    ssRes += (o.litresPerHour - predicted) ** 2;
    ssTot += o.litresPerHour ** 2;
  });

  return {
    ok: true,
    reason: null,
    capacityCoefficient: final.a,
    loadCoefficient: final.b,
    rSquared: ssTot > 0 ? 1 - ssRes / ssTot : null,
    nInput: obs.length,
    nFitted: kept.length,
    nTrimmed: obs.length - kept.length,
    // A non-positive capacity coefficient means this fleet's data shows no
    // size-related litres/hour penalty. Saying "you would save X litres by
    // downsizing" from it would be inventing a benefit, so it is refused.
    capacityCoefficientUsable: final.a > 0,
  };
};

// --- per-asset summary ------------------------------------------------------

/**
 * Roll a DG's intervals up into the row the report shows.
 *
 * @param {Object} params
 * @param {Object} params.asset descriptive fields (code, model, location…)
 * @param {Array} params.intervals output of buildAssetIntervals
 * @param {number|null} params.ratedKva confirmed rating, null when unconfirmed
 * @param {Object} params.assumptions
 * @param {Object} params.curve output of fitFleetFuelCurve
 * @param {number[]} params.sizes standard size ladder
 * @param {Object} params.prices  { [kva]: purchase price INR } — may be empty
 * @param {number|null} params.dieselPricePerLitre
 */
export const summariseAsset = ({
  asset,
  intervals,
  ratedKva,
  assumptions = DEFAULT_ASSUMPTIONS,
  curve = null,
  sizes = STANDARD_DG_SIZES_KVA,
  prices = {},
  dieselPricePerLitre = null,
}) => {
  const rows = intervals || [];
  const notes = [];

  const hours = rows.reduce((s, r) => s + r.hoursRun, 0);
  const energyKwh = rows.reduce((s, r) => s + r.kwhRun, 0);
  const fuelL = rows.reduce((s, r) => s + r.fuelL, 0);

  const avgKw = hours > 0 ? energyKwh / hours : null;
  const efficiencyKwhPerL = fuelL > 0 ? energyKwh / fuelL : null;
  const ratedKw = isNum(ratedKva) && ratedKva > 0 ? ratedKva * assumptions.powerFactor : null;
  const utilisation = avgKw !== null && ratedKw ? avgKw / ratedKw : null;

  const p95Kw = percentile(rows.map((r) => r.avgKw), 0.95);
  if (rows.length > 0 && rows.length < assumptions.minIntervalsForP95) {
    notes.push(
      `Only ${rows.length} valid interval(s) — the P95 load is a thin estimate and should not drive a purchase.`
    );
  }

  // Observed span, measured from the OPENING of the first interval to the
  // CLOSING of the last, because that is the stretch of time the litres and
  // hours actually cover.
  const observedDays =
    rows.length > 0
      ? (Math.max(...rows.map((r) => r.currentMs)) - Math.min(...rows.map((r) => r.previousMs))) / 86400000
      : null;

  // A DG whose overall efficiency is not physically believable has a metering
  // problem, not a sizing problem. It is reported, and it claims nothing.
  const meterSuspect =
    efficiencyKwhPerL !== null &&
    (efficiencyKwhPerL < assumptions.minPlausibleEfficiency || efficiencyKwhPerL > assumptions.maxPlausibleEfficiency);
  if (meterSuspect) {
    notes.push(
      `Overall efficiency ${efficiencyKwhPerL.toFixed(2)} kWh/L is outside the plausible ` +
        `${assumptions.minPlausibleEfficiency}–${assumptions.maxPlausibleEfficiency} kWh/L band — validate the kWh and hour metering before resizing.`
    );
  }

  const requiredKva =
    p95Kw !== null ? p95Kw / (assumptions.powerFactor * assumptions.targetLoading) : null;

  let recommendedKva = null;
  let recommendationReason = null;
  if (!isNum(ratedKva) || ratedKva <= 0) {
    recommendationReason = 'Rated kVA not confirmed';
  } else if (meterSuspect) {
    recommendedKva = ratedKva;
    recommendationReason = 'Metering must be validated before a size can be recommended';
  } else {
    const rec = recommendSize(requiredKva, ratedKva, sizes);
    recommendedKva = rec.kva;
    recommendationReason = rec.reason;
  }

  // Loading the candidate would carry at the same P95 — the number that says
  // whether the candidate is actually comfortable.
  const p95LoadingAfter =
    p95Kw !== null && isNum(recommendedKva) && recommendedKva > 0
      ? p95Kw / (recommendedKva * assumptions.powerFactor)
      : null;

  // Capacity-only saving: the litres/hour penalty attributable to size, over the
  // hours actually run, holding delivered load unchanged.
  let periodSavingL = null;
  let savingReason = null;
  if (!curve || !curve.ok) {
    savingReason = curve && curve.reason ? curve.reason : 'No fleet fuel curve';
  } else if (!curve.capacityCoefficientUsable) {
    savingReason = 'The fleet data shows no size-related litres/hour penalty, so no capacity saving can be claimed';
  } else if (meterSuspect) {
    savingReason = 'Metering suspect — no saving claimed';
  } else if (!isNum(recommendedKva) || !isNum(ratedKva)) {
    savingReason = recommendationReason || 'No recommended size';
  } else if (recommendedKva >= ratedKva) {
    periodSavingL = 0;
    savingReason = recommendationReason || 'Already right-sized';
  } else {
    periodSavingL = Math.max(0, curve.capacityCoefficient * (ratedKva - recommendedKva) * hours);
  }

  let annualisedSavingL = null;
  let annualisationReason = null;
  if (periodSavingL === null) {
    annualisationReason = savingReason;
  } else if (observedDays === null || observedDays <= 0) {
    annualisationReason = 'No observed period to annualise over';
  } else if (observedDays < assumptions.minAnnualisationDays) {
    annualisationReason = `Observed period is only ${observedDays.toFixed(1)} day(s); at least ${assumptions.minAnnualisationDays} are needed to state an annual figure`;
  } else {
    annualisedSavingL = (periodSavingL * 365) / observedDays;
  }

  // Money. Null — never zero — whenever the price behind it was not supplied.
  const replacementCostInr =
    isNum(recommendedKva) && isNum(ratedKva) && recommendedKva < ratedKva ? num(prices[String(recommendedKva)]) : null;
  const annualFuelValueInr =
    annualisedSavingL !== null && isNum(dieselPricePerLitre) ? annualisedSavingL * dieselPricePerLitre : null;
  const paybackMonths =
    isNum(replacementCostInr) && isNum(annualFuelValueInr) && annualFuelValueInr > 0
      ? (replacementCostInr / annualFuelValueInr) * 12
      : null;

  let action;
  if (!isNum(ratedKva) || ratedKva <= 0) {
    action = 'Confirm rated kVA';
  } else if (meterSuspect) {
    action = 'Validate metering first';
  } else if (isNum(recommendedKva) && isNum(ratedKva) && recommendedKva < ratedKva) {
    action = 'Confirm load study, then right-size';
  } else {
    action = 'Retain current rating';
  }

  return {
    key: asset.assetCode,
    ...asset,
    ratedKva: isNum(ratedKva) ? ratedKva : null,
    ratedKw,
    intervalCount: rows.length,
    hours,
    energyKwh,
    fuelL,
    avgKw,
    utilisation,
    efficiencyKwhPerL,
    p95Kw,
    requiredKva,
    recommendedKva,
    recommendationReason,
    p95LoadingAfter,
    observedDays,
    periodSavingL,
    savingReason,
    annualisedSavingL,
    annualisationReason,
    savingPercentOfFuel: periodSavingL !== null && fuelL > 0 ? periodSavingL / fuelL : null,
    replacementCostInr,
    annualFuelValueInr,
    paybackMonths,
    meterSuspect,
    action,
    notes,
  };
};

// --- portfolio --------------------------------------------------------------

/**
 * Portfolio totals over the DGs that could be analysed.
 *
 * Every total states how many assets it covers, so a figure can never be read
 * as covering the whole fleet when part of it was excluded.
 */
export const summarisePortfolio = (assetRows, assumptions = DEFAULT_ASSUMPTIONS) => {
  const rows = (assetRows || []).filter((r) => isNum(r.ratedKva) && r.intervalCount > 0);

  const installedKva = rows.reduce((s, r) => s + r.ratedKva, 0);
  const hours = rows.reduce((s, r) => s + r.hours, 0);
  const energyKwh = rows.reduce((s, r) => s + r.energyKwh, 0);
  const fuelL = rows.reduce((s, r) => s + r.fuelL, 0);

  // Weighted utilisation: total energy over the energy the installed sets could
  // have delivered in the hours they actually ran. Averaging per-asset
  // percentages instead would let a DG that ran for two hours count as much as
  // one that ran for a thousand.
  const availableKwh = rows.reduce((s, r) => s + r.ratedKva * assumptions.powerFactor * r.hours, 0);
  const weightedUtilisation = availableKwh > 0 ? energyKwh / availableKwh : null;

  const sizedRows = rows.filter((r) => isNum(r.recommendedKva));
  const recommendedKva = sizedRows.reduce((s, r) => s + r.recommendedKva, 0);

  const savingRows = rows.filter((r) => isNum(r.periodSavingL));
  const periodSavingL = savingRows.reduce((s, r) => s + r.periodSavingL, 0);

  const annualRows = rows.filter((r) => isNum(r.annualisedSavingL));
  const annualisedSavingL = annualRows.reduce((s, r) => s + r.annualisedSavingL, 0);

  const capexRows = rows.filter((r) => isNum(r.replacementCostInr));
  const replacementCostInr = capexRows.length ? capexRows.reduce((s, r) => s + r.replacementCostInr, 0) : null;

  const valueRows = rows.filter((r) => isNum(r.annualFuelValueInr));
  const annualFuelValueInr = valueRows.length ? valueRows.reduce((s, r) => s + r.annualFuelValueInr, 0) : null;

  // Portfolio payback covers the DGs proposed for a smaller set, and only when
  // EVERY one of them has both a purchase price and an annual fuel value. A
  // retained DG adds no capex and no saving, so its (true zero) fuel value must
  // not block the figure; a replacement with a missing price must, because the
  // payback would otherwise understate the spend.
  const replacementRows = rows.filter((r) => isNum(r.recommendedKva) && r.recommendedKva < r.ratedKva);
  const paybackComparable =
    replacementRows.length > 0 &&
    replacementRows.every((r) => isNum(r.replacementCostInr) && isNum(r.annualFuelValueInr));
  const replacementCapex = replacementRows.reduce((s, r) => s + (r.replacementCostInr || 0), 0);
  const replacementValue = replacementRows.reduce((s, r) => s + (r.annualFuelValueInr || 0), 0);
  const paybackMonths =
    paybackComparable && replacementValue > 0 ? (replacementCapex / replacementValue) * 12 : null;

  return {
    assetsIncluded: rows.length,
    assetsSized: sizedRows.length,
    installedKva,
    recommendedKva: sizedRows.length ? recommendedKva : null,
    capacityReduction:
      sizedRows.length === rows.length && installedKva > 0 ? 1 - recommendedKva / installedKva : null,
    hours,
    energyKwh,
    fuelL,
    weightedUtilisation,
    avgKw: hours > 0 ? energyKwh / hours : null,
    efficiencyKwhPerL: fuelL > 0 ? energyKwh / fuelL : null,
    periodSavingL: savingRows.length ? periodSavingL : null,
    periodSavingAssets: savingRows.length,
    annualisedSavingL: annualRows.length ? annualisedSavingL : null,
    annualisedSavingAssets: annualRows.length,
    savingPercentOfFuel: savingRows.length && fuelL > 0 ? periodSavingL / fuelL : null,
    replacementCostInr,
    replacementCostAssets: capexRows.length,
    annualFuelValueInr,
    annualFuelValueAssets: valueRows.length,
    paybackMonths,
  };
};

// --- utilisation over time --------------------------------------------------

/**
 * One line per DG for the utilisation-over-time chart.
 *
 * Each point is ONE measured interval, placed at its closing full tank, carrying
 * the average utilisation over that whole interval. Nothing between two points
 * is measured, and the chart must not suggest otherwise:
 *
 *   - Consecutive measured intervals share a full tank (one closes where the
 *     next opens), so joining them is honest.
 *   - When an interval between them was excluded, the next measured interval
 *     does NOT open where the previous one closed. A break point (utilisation
 *     null) is inserted at that opening so the line stops instead of drawing
 *     across the unmeasured stretch.
 *   - A DG with no confirmed rating has no utilisation at all. It gets no line
 *     and is returned in `noRating` so the chart can say it was left out.
 *
 * @param {Array} intervals output of buildAssetIntervals (any number of assets)
 * @returns {{series: Array<{assetCode: string, points: Array, breaks: number,
 *            intervalCount: number}>, noRating: string[]}}
 */
export const buildUtilisationSeries = (intervals) => {
  const byAsset = {};
  (intervals || []).forEach((iv) => {
    if (!byAsset[iv.assetCode]) byAsset[iv.assetCode] = [];
    byAsset[iv.assetCode].push(iv);
  });

  const series = [];
  const noRating = [];
  Object.keys(byAsset)
    .sort()
    .forEach((assetCode) => {
      const measured = byAsset[assetCode]
        .filter((iv) => isNum(iv.utilisation))
        .sort((a, b) => a.currentMs - b.currentMs);
      if (measured.length === 0) {
        noRating.push(assetCode);
        return;
      }

      const points = [];
      let breaks = 0;
      let previous = null;
      measured.forEach((iv) => {
        if (previous && iv.previousMs !== previous.currentMs) {
          points.push({ ms: iv.previousMs, utilisation: null, isBreak: true });
          breaks += 1;
        }
        points.push({
          ms: iv.currentMs,
          previousMs: iv.previousMs,
          // The whole interval row, so hovering a point can show its full working.
          interval: iv,
          utilisation: iv.utilisation,
          avgKw: iv.avgKw,
          ratedKva: iv.ratedKva,
          hoursRun: iv.hoursRun,
          isBreak: false,
        });
        previous = iv;
      });

      series.push({ assetCode, points, breaks, intervalCount: measured.length });
    });

  return { series, noRating };
};

// --- recommended-rating check -------------------------------------------------

/**
 * How many intervals averaged a load above a given rating — used to show
 * management how often a proposed smaller set would have been overloaded even
 * on the interval AVERAGE (peaks inside an interval are not visible here).
 *
 * @returns {{above: number, total: number, limitKw: number}|null} null when the
 *          rating or power factor is not usable, never a guessed count.
 */
export const countIntervalsAboveRating = (intervals, kva, powerFactor) => {
  if (!isNum(kva) || kva <= 0 || !isNum(powerFactor) || powerFactor <= 0) return null;
  const limitKw = kva * powerFactor;
  const measured = (intervals || []).filter((iv) => iv && isNum(iv.avgKw));
  return { above: measured.filter((iv) => iv.avgKw > limitKw).length, total: measured.length, limitKw };
};

// --- meter label passthrough ------------------------------------------------
// Re-exported so the report renders meter units from the one shared definition.
export { METER_LABELS };
